import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";

export default function Gallery({ images = [], name = "", captions = [] }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const cap = (i) => captions[i] || "";
  const alt = (i) => captions[i] || `${name} — foto ${i + 1}`;

  const go = useCallback((d) => setIdx((i) => (i + d + images.length) % images.length), [images.length]);
  const openAt = (i) => { setIdx(i); setOpen(true); };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [open, go]);

  if (!images.length) return null;
  const thumbs = images.slice(1, 5);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 h-auto md:h-[480px]">
        <button data-testid="gallery-main" onClick={() => openAt(0)} className="relative group overflow-hidden rounded-2xl h-[280px] md:h-full">
          <img src={images[0]} alt={alt(0)} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
        </button>
        <div className="hidden md:grid grid-cols-2 grid-rows-2 gap-3">
          {thumbs.map((src, i) => (
            <button key={i} onClick={() => openAt(i + 1)} className="relative group overflow-hidden rounded-2xl">
              <img src={src} alt={alt(i + 1)} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
              {i === thumbs.length - 1 && images.length > 5 && (
                <span className="absolute inset-0 bg-black/45 flex items-center justify-center text-white text-sm font-medium">+{images.length - 5} foto</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <button data-testid="gallery-show-all" onClick={() => openAt(0)} className="mt-3 inline-flex items-center gap-2 border border-brand-olive text-brand-olive hover:bg-brand-ivory transition-colors rounded-full px-5 py-2.5 text-sm font-medium">
        <Images className="w-4 h-4" /> Mostra tutte le foto ({images.length})
      </button>

      {open && (
        <div role="dialog" aria-modal="true" aria-label={`Galleria ${name}`} data-testid="lightbox" className="fixed inset-0 z-[80] bg-black/95 flex flex-col">
          <div className="flex items-center justify-between p-4 text-white/90">
            <span className="text-sm" data-testid="lightbox-counter">{idx + 1} / {images.length}</span>
            <button data-testid="lightbox-close" onClick={() => setOpen(false)} aria-label="Chiudi galleria" className="p-2 hover:text-white"><X className="w-6 h-6" /></button>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 pb-6 relative">
            <button data-testid="lightbox-prev" onClick={() => go(-1)} aria-label="Foto precedente" className="absolute left-3 md:left-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronLeft className="w-6 h-6" /></button>
            <img src={images[idx]} alt={alt(idx)} className="max-h-[80vh] max-w-[92vw] object-contain rounded-lg" />
            <button data-testid="lightbox-next" onClick={() => go(1)} aria-label="Foto successiva" className="absolute right-3 md:right-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronRight className="w-6 h-6" /></button>
          </div>
          {cap(idx) && <div className="pb-6 text-center text-white/85 text-sm" data-testid="lightbox-caption">{cap(idx)}</div>}
        </div>
      )}
    </>
  );
}
